<template>
    <div class="main-container">
        <div class="chart-container">
            <component
                :is="selectedChart"
                v-bind="componentDataProps"
                ref="chartComponent"
                :chartData="chartData"
                :attribute="attributes[0]"
                :chartCreationProps="chartCreationProps"
                :chartLoading="chartLoading"
                @own-filter-update="setOwnFilters"
                @dashboard-filter-update="setDashboardFilters"
                @get-chart-data="getChartData"
            />
        </div>
        <q-inner-loading :showing="chartLoading" />
    </div>
</template>
<script>
import {
    Api, apiRequest, notifyError,
} from '@/api'
import { serializeFilterForApi } from '@/utils/filters'
import dashboardChartsMixin from '@/mixins/dashboardCharts.mixin'
import { ChartTypes } from '../utils'
import BarChart from './BarChart.vue'
import AttributeChart from './AttributeChart.vue'
import HeatMapChart from './HeatMapChart.vue'
import FrequencyDurationChart from './FrequencyDurationChart.vue'
import ScatterChart from './ScatterChart.vue'

export default {
    name: 'GraphComponent',

    components: {
        BarChart,
        AttributeChart,
        FrequencyDurationChart,
        ScatterChart,
    },

    mixins: [dashboardChartsMixin],

    props: {
        chartCreationProps: {
            type: Object,
            required: true,
        },
        generalFilters: {
            type: [Array, Object],
            required: true,
        },
        generalFilterSets: {
            type: Array,
            required: true,
        },
        generalOperator: {
            type: String,
            required: true,
        },
        dashboardFilters: {
            type: Object,
            required: true,
        },
        graphFilters: {
            type: [Array, String],
            required: true,
        },
        categoricalAttributes: {
            type: Array,
            required: false,
            default: () => [],
        },
        attributes: {
            type: Array,
            required: false,
            default: () => [],
        },
        numericAttributes: {
            type: Array,
            required: false,
            default: () => [],
        },
        graphObj: {
            type: String,
            required: false,
            default: () => undefined,
        },
        graphValues: {
            type: String,
            required: false,
            default: () => undefined,
        },
    },

    emits: ['own-filter-update, dashboard-filter-update'],

    data() {
        return {
            DEFAULT_VALUES: {
                freqDuration: {
                    duration: {
                        key: "MONTH",
                        label: this.$t("visualization.customPlotPanels.attributesInfo.month"),
                    },
                    rangeSel: {
                        key: "START",
                        label: this.$t("visualization.customPlotPanels.attributesInfo.start"),
                    },
                },
                sortParams: {
                    sortBy: { key: "FREQUENCY", label: "Frequency" },
                    sortDirection: { key: "ASCENDING", label: "Ascending" },
                },
            },
            chartData: {},
            chartLoading: false,
            hasSortBy: false,
        }
    },

    computed: {
        selectedChart() {
            switch (this.chartCreationProps.graphType) {
                case ChartTypes.BAR:
                    return BarChart
                case ChartTypes.FREQUENCY_AND_DURATION:
                    return FrequencyDurationChart
                case ChartTypes.HEATMAP:
                    return HeatMapChart
                case ChartTypes.ATTRIBUTE:
                    return AttributeChart
                case ChartTypes.SCATTER:
                    return ScatterChart
                default:
                    return AttributeChart
            }
        },
        componentDataProps() {
            const {
                affectedByGlobalFilters,
                affectedByDashboardFilters,
                ownFilters,
                graphType,
                graphGroup,
                i,
            } = this.chartCreationProps

            const generalFiltering = {
                filterSets: affectedByGlobalFilters ? this.generalFilterSets || [] : [],
                operator: this.generalOperator,
                ...(affectedByGlobalFilters && { generalFilters: this.generalFilters }),
                ...(affectedByDashboardFilters && { dashboardFilters: this.dashboardFilters }),
                ...(ownFilters?.length > 0 && { ownFilters: this.graphFilters }),
            }

            return {
                index: i,
                ...(graphType !== ChartTypes.ATTRIBUTE && { graphGroup }),
                generalFiltering,
            }
        },
    },
    watch: {
        dashboardFilters: {
            handler() {
                this.updateData()
            },
            deep: true,
        },
        graphFilters: {
            handler() {
                if (this.graphObj === this.chartCreationProps.i) {
                    this.updateData()
                }
            },
            deep: true,
        },
    },
    mounted() {
        this.hasSortBy = !['AttributeChart', 'FrequencyDurationChart', 'ScatterChart'].includes(this.selectedChart.name)
        this.updateData()
    },
    methods: {
        normalizeDashboardFilters(filters) {
            const list = Array.isArray(filters) ? filters : (filters ? [filters] : [])
            return list.map((f) => (serializeFilterForApi(f) || f))
        },
        updateData() {
            let params
            if (this.hasSortBy) {
                params = {}
                if (this.chartCreationProps.sortBy || this.chartCreationProps.graphValues) {
                    params = {
                        sortBy: this.chartCreationProps.sortBy ? this.chartCreationProps.sortBy : this.chartCreationProps.graphValues.sortBy,
                        // eslint-disable-next-line max-len
                        sortDirection: this.chartCreationProps.graphValues.sortDirection ? this.chartCreationProps.graphValues.sortDirection : 'DESCENDING',
                        ownFilters: this.chartCreationProps.ownFilters.length === 0 ? [] : this.chartCreationProps.ownFilters,
                    }
                }
            } else if (this.selectedChart.name === 'AttributeChart' && (this.chartCreationProps.sortBy || this.chartCreationProps.graphValues)) {
                const { graphValues } = this.chartCreationProps
                const graphVal = JSON.parse(graphValues)
                params = {
                    attribute: graphVal.attribute,
                    ownFilters: this.chartCreationProps.ownFilters.length === 0 ? [] : this.chartCreationProps.ownFilters,
                    ...(graphVal.numericAttribute
                        ? {
                            startValue: graphVal.numericRange.min,
                            endValue: graphVal.numericRange.max,
                        }
                        : {
                            sortBy: this.chartCreationProps.sortBy || graphVal.sortBy || 'FREQUENCY',
                            limit: 15,
                            start: 0,
                        }
                    ),
                }
            } else if (this.selectedChart.name === "ScatterChart") {
                params = {
                    sortBy: this.chartCreationProps.sortBy ? this.chartCreationProps.sortBy : this.chartCreationProps.graphValues.sortBy,
                    // eslint-disable-next-line max-len
                    sortDirection: 'DESCENDING',
                    additionalDimensions: [{ type: 'DURATION', measurement: 'AVERAGE' }],
                    baseDimension: { type: 'FREQUENCY' },
                    ownFilters: this.chartCreationProps.ownFilters.length === 0 ? [] : this.chartCreationProps.ownFilters,
                }
            } else if (this.selectedChart.name === "FrequencyDurationChart") {
                params = {
                    limit: 8,
                    start: 0,
                    sortBy: this.chartCreationProps.sortBy ? this.chartCreationProps.sortBy : 'FREQUENCY',
                    caseType: JSON.parse(this.chartCreationProps.graphValues).caseType,
                    duration: JSON.parse(this.chartCreationProps.graphValues).duration,
                    ownFilters: this.chartCreationProps.ownFilters.length === 0 ? [] : this.chartCreationProps.ownFilters,
                }
            }
            this.getChartData(params)
        },
        setOwnFilters(filters) {
            this.$emit('own-filter-update', this.normalizeDashboardFilters(filters))
        },

        setDashboardFilters(filters) {
            this.$emit('dashboard-filter-update', this.normalizeDashboardFilters(filters))
        },

        async getChartData(chartParams) {
            this.chartLoading = true
            const { processId } = this.$route.params
            const generalParams = {
                datasetUid: processId,
                graphType: this.chartCreationProps.graphType,
                graphGroup: this.chartCreationProps.graphGroup,
            }
            const params = {
                ...generalParams,
                ...chartParams,
                ...this.componentDataProps.generalFiltering,
            }

            apiRequest(Api().datasets.getDashboardGraph({ params }))
                .then((data) => {
                    this.chartData = { ...data }
                }).catch((error) => {
                    this.chartData = {}
                    notifyError(error)
                })
                .finally(() => {
                    this.chartLoading = false
                })
        },
    },
}
</script>
<style lang="scss">
    .main-container {
        width: 100%;
        height: 100%;
        background-color: #fbfbfb;
        border-radius: 5px;
    }
</style>
